"use client";
import { useMemo } from "react";
import ProductCarousel from "@/app/components/common-builder/product/product-carousel/ProductCarousel";
import ProductGrid from "@/app/components/common-builder/product/product-grids/ProductGrid";
import BundleAssociatedProduct from "@/app/components/pages-components/common/associated-products/bundle-associated-product";
import type { CartCrossSell } from "./useCartCrossSell";

interface Props {
  crossSell: CartCrossSell;
  /** Follows the merchant's `crosssell_products_asgrid` in both view modes. */
  asGrid: boolean;
}

/**
 * The products themselves. Shared by the inline section and the popup so the two
 * view modes can never drift apart; each owns its own heading.
 *
 * The endpoint returns a plain array, so there is nothing to paginate.
 */
const CrossSellPanel: React.FC<Props> = ({ crossSell, asGrid }) => {
  const { theme, size, products } = crossSell;

  // Bundles can't render as a product card — they get their own configurator
  // below the list, as on the product page's associated-products block.
  const { bundles, cards } = useMemo(
    () => ({
      bundles: products.filter((product) => product.type === "bundle"),
      cards: products.filter((product) => product.type !== "bundle"),
    }),
    [products],
  );

  if (!theme) return null;

  const productList = { data: cards, configurations: { style_type: size } };

  return (
    <div className="crosssell-panel">
      {cards.length > 0 &&
        (asGrid ? (
          <ProductGrid products={productList} theme={theme} />
        ) : (
          <ProductCarousel products={productList} theme={theme} />
        ))}

      {bundles.length > 0 && (
        <div className="d-flex flex-column gap-4">
          {bundles.map((bundle) => (
            <BundleAssociatedProduct key={bundle.id} slug={bundle.slug} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CrossSellPanel;
